import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Copy, X, ExternalLink, Check } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ShareModal({ messageId, onClose }) {
    const [loading, setLoading] = useState(true);
    const [shareData, setShareData] = useState(null);
    const [insight, setInsight] = useState(null);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const r = await axios.post(`${API}/share/create`, {
                    message_id: messageId,
                });
                if (cancelled) return;
                setShareData(r.data);
                const g = await axios.get(`${API}/share/${r.data.token}`);
                if (!cancelled) setInsight(g.data);
            } catch (e) {
                toast.error("Couldn't create a share link.");
                onClose?.();
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [messageId, onClose]);

    const shareUrl = shareData
        ? `${window.location.origin}${shareData.url_path}`
        : "";

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(shareUrl);
            setCopied(true);
            toast.success("Link copied");
            setTimeout(() => setCopied(false), 2000);
        } catch {
            toast.error("Couldn't copy. Select & copy manually.");
        }
    };

    return (
        <div
            className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 sm:p-6 bg-black/70 backdrop-blur-sm"
            onClick={onClose}
            data-testid="share-modal-overlay"
        >
            <div
                className="relative w-full max-w-lg bg-rt-surface border border-rt-border rounded-3xl p-6 sm:p-8 shadow-2xl"
                onClick={(e) => e.stopPropagation()}
                data-testid="share-modal"
            >
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 text-rt-muted hover:text-rt-text transition"
                    aria-label="Close"
                    data-testid="share-modal-close"
                >
                    <X className="w-5 h-5" />
                </button>

                <div className="text-xs uppercase tracking-[0.2em] text-rt-dim mb-3">
                    Share this insight
                </div>
                <div className="font-serif text-2xl tracking-tight mb-5">
                    A quiet nudge, made shareable.
                </div>

                {loading && (
                    <div className="py-10 text-center text-rt-muted text-sm">
                        Crafting your card…
                    </div>
                )}

                {insight && (
                    <div
                        className="rounded-2xl bg-rt-card border border-rt-border p-5 mb-5"
                        data-testid="share-modal-preview"
                    >
                        <div className="font-serif text-lg leading-snug text-rt-text">
                            {insight.headline}
                        </div>
                        {insight.suggestion && (
                            <div className="mt-3 text-sm text-rt-muted italic leading-relaxed">
                                “{insight.suggestion}”
                            </div>
                        )}
                        <div className="mt-4 text-[10px] uppercase tracking-[0.25em] text-rt-accent/70">
                            realtalk · anonymous
                        </div>
                    </div>
                )}

                {shareData && (
                    <div className="space-y-3">
                        <div className="flex items-center gap-2 bg-rt-card border border-rt-border rounded-full px-4 py-2.5">
                            <input
                                readOnly
                                value={shareUrl}
                                className="flex-1 bg-transparent outline-none text-sm text-rt-muted truncate"
                                data-testid="share-modal-url"
                            />
                            <button
                                onClick={handleCopy}
                                className="shrink-0 inline-flex items-center gap-1.5 rounded-full bg-[#B392F0] hover:bg-[#C8B1F5] text-black text-xs font-medium px-3 py-1.5 transition"
                                data-testid="share-modal-copy-btn"
                            >
                                {copied ? (
                                    <Check className="w-3.5 h-3.5" />
                                ) : (
                                    <Copy className="w-3.5 h-3.5" />
                                )}
                                {copied ? "Copied" : "Copy"}
                            </button>
                        </div>
                        <a
                            href={shareUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 text-xs text-rt-muted hover:text-rt-text transition"
                            data-testid="share-modal-open-link"
                        >
                            Open card <ExternalLink className="w-3 h-3" />
                        </a>
                    </div>
                )}

                <div className="mt-6 text-[11px] text-rt-dim leading-relaxed">
                    The shared card is an anonymized snippet — no conversation
                    context is exposed.
                </div>
            </div>
        </div>
    );
}
